/**
 * @typedef {Object} DataService
 *
 * @property {function} getContent
 * @property {function} getCategoriesListing
 * @property {function} getGlobals
 * @property {function} getCountries
 * @property {function} getCourses
 * @property {function} getClientsCountryCode
 * @property {function} getInternationalProgramme
 * @property {function} getCurrencyExchangeRate
 */


(function() {
    'use strict';

    angular.module( APP_ID )
        .factory( 'DataService', [ '$q', '$resource', 'LoggerFactory', 'App', 'Utils', 'CONFIG', ExternalDataService ]);


    /**
     * Fetching data & content from the external API services and prepares them
     * for Controllers and Directives.
     *
     * @param {*} $q
     * @param {*} $resource
     * @param {LoggerFactory} LoggerFactory
     * @param {App} App
     * @param {Utils} Utils
     * @param {Object} CONFIG
     *
     * @returns {DataService}
     */
    function ExternalDataService(
        $q,
        $resource,
        LoggerFactory,
        App,
        Utils,
        CONFIG
    ){





        /* ===== PRIVATE MEMBERS ============================================ */


        /** @type {Logger} */
        var logger = LoggerFactory.createLogger( 'DataService' );


        /** @constant */
        var RESPONSE_HEADER_GEO = 'x-geo-location';


        /** @type {Object} */
        var clientsCountryCode = new ClientsCountryCode();





        /* ===== SERVICE'S PUBLIC API ======================================= */


        /** @type {DataService} */
        var publicAPI = {
            getContent:     getContent,
            getCategoriesListing: getCategoriesListing,
            getGlobals:      getGlobals,
            getCountries:   getCountries,
            getCourses:     getCourses,

            getClientsCountryCode:      getClientsCountryCode,

            getInternationalProgramme:  getInternationalProgramme,
            getCurrencyExchangeRate:    getCurrencyExchangeRate
        }





        ////////////////////////////////////////////////////////////////////////

        return publicAPI; // Expose the API

        ////////////////////////////////////////////////////////////////////////





        /* ===== PUBLIC METHODS ============================================= */


        /**
         * Makes a CORS request to the CMS API to retrieve a standard
         * content page data.
         *
         * @param {{viewUrl: string}} urlParams
         *
         * @returns {PromiseLike<Object>}
         */
        function getContent( urlParams ) {

            var requestSettings = {
                get: {
                    params: { 'q': urlParams.viewUrl },
                    timeout: CONFIG.API.TIMEOUT_IN_MS
                },
            };

            // CORS with cookie *ONLY* in administration mode
            if ( App.getContentMode() === CONFIG.MODES.ADMIN ) {
                requestSettings.get.withCredentials = true;
            }

            return makeApiCall( Utils.makeContentAPIUrl( urlParams.urlPath ), requestSettings );
        }


        /**
         * Makes a CORS request to the CMS API to retrieve a metadata for
         * application's global information (main navigation structure, contacts, meta information etc.).
         *
         * @returns {PromiseLike<Object>}
         */
        function getGlobals() {
            // CORS with cookie *ONLY* in administration mode
            if ( App.getContentMode() === CONFIG.MODES.ADMIN ) {
                return makeApiCall( Utils.makeContentAPIUrl( CONFIG.API.CMS.PATHS.GLOBAL ), { get: { withCredentials: true }});
            } else {
                return makeApiCall( Utils.makeContentAPIUrl( CONFIG.API.CMS.PATHS.GLOBAL, CONFIG.MODES.PUBLIC.MODE_ID ));
            }
        }


        /**
         * Makes a CORS request to the CMS API to retrieve list of all
         * supported countries, their ISO codes and currencies they use.
         *
         * @returns {PromiseLike<Object>}
         */
        function getCountries() {
            logger.debug( "Retrieving list of countries from Squiz Content API..." );

            var requestSettings = {
                get: {
                    params:     {},
                    isArray:    true,
                    timeout:    CONFIG.API.TIMEOUT_IN_MS
                }
            }

            // CORS with cookie *ONLY* in administration mode
            if ( App.getContentMode() === CONFIG.MODES.ADMIN ) {
                requestSettings.get.withCredentials = true;
            }

            return makeApiCall( Utils.makeContentAPIUrl( CONFIG.API.CMS.PATHS.COUNTRIES ), requestSettings );
        }


        /**
         * Makes a CORS request to the CMS API to retrieve list of all
         * existing courses in a shortened format (few basic information).
         *
         * @returns {PromiseLike<Object>}
         */
        function getCourses() {
            logger.debug( "Retrieving list of all existing courses from Squiz Content API..." );

            var requestSettings = {
                get: {
                    params:     {},
                    timeout:    CONFIG.API.TIMEOUT_IN_MS
                }
            }

            // CORS with cookie *ONLY* in administration mode
            if ( App.getContentMode() === CONFIG.MODES.ADMIN ) {
                requestSettings.get.withCredentials = true;
            }

            return makeApiCall( Utils.makeContentAPIUrl( CONFIG.API.CMS.PATHS.COURSES, CONFIG.MODES.PUBLIC.MODE_ID ), requestSettings );
        }


        /**
         * Makes a request to the International API to retrieve a
         * programme information (fees, requirements, basic information etc.)
         *
         * @param {string} programmeCode
         * @param {string} countryCode
         *
         * @returns {PromiseLike<Object>}
         */
        function getInternationalProgramme( programmeCode, countryCode ) {
            logger.debug( "Retrieving Programme information from International API..." );

            var parametersObject = CONFIG.API.INTERNATIONAL.PARAMS.PROGRAMME;

            parametersObject.programme  = programmeCode;
            parametersObject.country    = countryCode;

            var requestSettings = {
                get: {
                    params: {}, // Params as an URL path!
                    timeout: CONFIG.API.TIMEOUT_IN_MS
                }
            };

            return makeApiCall( Utils.makeInternationalAPIUrl() + angular.toJson( parametersObject ), requestSettings );
        }


        /**
         *
         * Makes a CORS request to the CMS API to retrieve list of all exchange
         * rates between source currency (defined by 3-leter currency code) and all other existing currencies.
         *
         * @param {string} sourceCurrencyCode
         *
         * @returns {PromiseLike<Object>}
         */
        function getCurrencyExchangeRate( sourceCurrencyCode ) {
            logger.debug( "Retrieving conversion rates between `%s` and all other currencies...", sourceCurrencyCode );

            var requestSettings = {
                get: {
                    timeout: CONFIG.API.TIMEOUT_IN_MS
                }
            };

            // CORS with cookie *ONLY* in administration mode
            if ( App.getContentMode() === CONFIG.MODES.ADMIN ) {
                requestSettings.get.withCredentials = true;
            }

            return makeApiCall( Utils.makeContentAPIUrl( CONFIG.API.CMS.PATHS.EXCHANGE_RATES + '/' + sourceCurrencyCode.toLowerCase() ), requestSettings );
        }


        /** @returns {PromiseLike<Object>} */
        function getCategoriesListing() {
            var requestSettings = {
                get: {
                    params: {},
                    timeout: CONFIG.API.TIMEOUT_IN_MS
                }
            };

            // CORS with cookie *ONLY* in administration mode
            if ( App.getContentMode() === CONFIG.MODES.ADMIN ) {
                requestSettings.get.withCredentials = true;
            }

            return makeApiCall( Utils.makeContentAPIUrl( CONFIG.API.CMS.PATHS.CATEGORIES_LISTING ), requestSettings );
        }


        /**
         * @returns {Promise<String>}
         */
        function getClientsCountryCode() {
            return clientsCountryCode.getPromise();
        }




        /* ===== PRIVATE FUNCTIONS ========================================== */


        /**
         * Makes a safe API call, processes the response and returns a
         * $response's `Promise`.
         *
         * @param {string} requestUrl Full URL of the API endpoint that should
         * be called.
         * @param {Object} requestSettingsObject Object with request parameters
         * (such parameters, timeout etc.)
         *
         * @returns {PromiseLike<Object>}
         */
        function makeApiCall( requestUrl, requestSettingsObject ) {
            var requestSettingsObject = requestSettingsObject || {},

            apiCaller = $resource( requestUrl, {}, requestSettingsObject );

            // Returns processed $Promise
            return Utils.handleResponsePromise( apiCaller.get( {}, interceptResponseHeaders ).$promise );
        }


        function interceptResponseHeaders( responseObject, responseHeadersGetter ){
            processGeoLocationHeader( responseHeadersGetter( RESPONSE_HEADER_GEO ) );
        }


        function processGeoLocationHeader( countryCode ){
            if ( clientsCountryCode.isFulfilled() || !countryCode ) return;

            clientsCountryCode.set( countryCode );
        }



        function ClientsCountryCode() {
            var defer   = $q.defer(),
            fulfilled  = false;

            this.getPromise = function getPromise() {
                return defer.promise;
            };

            this.set = function  setCountryCode( countryCode ) {
                if ( angular.isString( countryCode ) && countryCode.length == 2 ) {
                    defer.resolve( countryCode );
                } else {
                    defer.reject( "Not a valid country code `%s`! Must meet ISO 3166-1 alpha-2 specification.", countryCode );
                }

                fulfilled = true;
            };


            this.isFulfilled = function isFulfilled() {
                return fulfilled;
            };
        }


    }
})();
